'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { LeadCaptureForm } from '@/components/calculator/LeadCaptureForm';

// Each option carries a risk weight (higher = more risk) and a gap flag that
// surfaces a specific recommendation when chosen.
interface Option {
  text: string;
  risk: number; // 0 (good) .. 3 (worst)
  gap?: string; // recommendation triggered when this option is selected
}

interface Question {
  id: string;
  text: string;
  helpText?: string;
  options: Option[];
}

const QUESTIONS: Question[] = [
  {
    id: 'mfa_coverage',
    text: 'How widely is multi-factor authentication (MFA) enforced?',
    helpText: 'MFA on email and admin accounts blocks the large majority of credential-based attacks.',
    options: [
      { text: 'Enforced on every account and app', risk: 0 },
      { text: 'On email and a few critical apps only', risk: 1, gap: 'Extend MFA enforcement beyond email to every business app—gaps are where attackers get in.' },
      { text: 'Optional / left up to each employee', risk: 3, gap: 'MFA is optional today. Enforce it organization-wide; optional MFA means most accounts have none.' },
      { text: 'Not in use / not sure', risk: 3, gap: 'No enforced MFA is the single biggest exposure here. Turn it on everywhere as the first move.' },
    ],
  },
  {
    id: 'password_manager',
    text: 'Does your team use a business password manager?',
    helpText: 'A managed password manager replaces reused passwords and insecure storage.',
    options: [
      { text: 'Yes—deployed and used by everyone', risk: 0 },
      { text: 'Some people use one, but it is not standard', risk: 2, gap: 'Standardize one business password manager for all staff, not a patchwork of personal tools.' },
      { text: 'People save passwords in browsers or spreadsheets', risk: 3, gap: 'Browser-saved and spreadsheet passwords are easily exfiltrated. Move everyone onto a managed password vault.' },
      { text: 'No password manager at all', risk: 3, gap: 'No password manager means weak, reused passwords by default. Deploying one is the highest-leverage fix.' },
    ],
  },
  {
    id: 'shared_reused',
    text: 'How are shared and reused passwords handled?',
    helpText: 'Shared logins over chat/email and reused passwords are a top breach vector.',
    options: [
      { text: 'Shared logins live in encrypted vaults; reuse is prevented', risk: 0 },
      { text: 'Some shared passwords are still emailed or messaged around', risk: 2, gap: 'Shared passwords are being sent over chat/email. Move them into encrypted shared vaults and rotate them.' },
      { text: 'Reused passwords across accounts are common', risk: 3, gap: 'Password reuse means one leak unlocks many accounts. Enforce unique passwords via the password manager.' },
      { text: 'Not sure', risk: 2, gap: 'No visibility into shared/reused passwords. An audit will surface where logins are duplicated or shared.' },
    ],
  },
  {
    id: 'breach_exposure',
    text: 'Do you monitor for company credentials exposed in breaches?',
    helpText: 'Leaked passwords from other sites are sold and reused against your accounts.',
    options: [
      { text: 'Yes—we get alerts and force resets on exposure', risk: 0 },
      { text: 'We check occasionally / manually', risk: 1, gap: 'Make breach monitoring continuous and tied to a forced-reset workflow, not an occasional manual check.' },
      { text: 'No monitoring in place', risk: 2, gap: 'No breach monitoring means exposed credentials sit usable for months. Add dark-web/breach monitoring.' },
      { text: 'Not sure', risk: 2, gap: 'Unknown breach exposure. Run a credential-exposure check against known breach data as a baseline.' },
    ],
  },
  {
    id: 'offboarding',
    text: 'When someone leaves, how fast is their access revoked?',
    helpText: 'Lingering accounts and shared passwords are a common path back in.',
    options: [
      { text: 'Same day, across all apps and shared vaults', risk: 0 },
      { text: 'Within a few days', risk: 2, gap: 'Tighten offboarding to same-day. A multi-day gap leaves active credentials for a departed employee.' },
      { text: 'Eventually / inconsistently', risk: 3, gap: 'Inconsistent offboarding leaves orphaned accounts. Centralize access so it can be revoked instantly.' },
      { text: 'Shared passwords are rarely changed when staff leave', risk: 3, gap: 'Departing staff retain shared passwords. Rotate shared secrets on every departure—ideally automatically.' },
    ],
  },
  {
    id: 'sso',
    text: 'Do you use single sign-on (SSO) for your core apps?',
    helpText: 'SSO centralizes access control and cuts the number of passwords in play.',
    options: [
      { text: 'Yes—core apps are behind SSO', risk: 0 },
      { text: 'A few apps, but most have separate logins', risk: 1, gap: 'Expand SSO coverage so access for core apps is centralized and instantly revocable.' },
      { text: 'No SSO—every app has its own login', risk: 2, gap: 'Without SSO, access is scattered across dozens of logins. Connecting core apps to SSO reduces risk and friction.' },
      { text: 'Not sure', risk: 2, gap: 'SSO status unclear. Centralizing identity behind SSO simplifies enforcement and offboarding.' },
    ],
  },
];

type Status = 'intro' | 'assessment' | 'results';

interface Answer {
  questionId: string;
  optionIndex: number;
}

function levelFor(percent: number): {
  level: 'High' | 'Medium' | 'Low';
  color: string;
  bg: string;
  blurb: string;
} {
  if (percent >= 55) {
    return {
      level: 'High',
      color: 'text-red-700 dark:text-red-300',
      bg: 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800',
      blurb:
        'Your identity controls have serious gaps—the kind attackers exploit first. Enforcing MFA and deploying a managed password manager should be an immediate priority.',
    };
  }
  if (percent >= 25) {
    return {
      level: 'Medium',
      color: 'text-amber-700 dark:text-amber-300',
      bg: 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800',
      blurb:
        'You have some controls in place but meaningful gaps remain. Closing them—especially MFA coverage and password hygiene—will materially lower your breach risk.',
    };
  }
  return {
    level: 'Low',
    color: 'text-green-700 dark:text-green-300',
    bg: 'bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-800',
    blurb:
      'Your identity posture is strong. A managed service keeps it that way—enforcing policy, monitoring exposure, and handling onboarding/offboarding as you grow.',
  };
}

export default function RiskCheckClient() {
  const [status, setStatus] = useState<Status>('intro');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [userCount, setUserCount] = useState<number>(25);

  const handleAnswer = (optionIndex: number) => {
    const question = QUESTIONS[currentIndex];
    const newAnswer: Answer = { questionId: question.id, optionIndex };
    const newAnswers = [...answers.filter((a) => a.questionId !== question.id), newAnswer];
    setAnswers(newAnswers);

    if (currentIndex < QUESTIONS.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setStatus('results');
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const handleRestart = () => {
    setAnswers([]);
    setCurrentIndex(0);
    setStatus('intro');
  };

  const result = useMemo(() => {
    if (answers.length !== QUESTIONS.length) return null;
    let total = 0;
    const maxPerQuestion = 3;
    const gaps: string[] = [];
    QUESTIONS.forEach((q) => {
      const a = answers.find((ans) => ans.questionId === q.id);
      if (!a) return;
      const option = q.options[a.optionIndex];
      total += option.risk;
      if (option.gap) gaps.push(option.gap);
    });
    const max = QUESTIONS.length * maxPerQuestion;
    const percent = Math.round((total / max) * 100);
    return { percent, gaps, ...levelFor(percent) };
  }, [answers]);

  const resultsSummary = useMemo(() => {
    if (!result) return '';
    const answerLines = QUESTIONS.map((q) => {
      const a = answers.find((ans) => ans.questionId === q.id);
      const chosen = a ? q.options[a.optionIndex].text : 'N/A';
      return `  • ${q.text} → ${chosen}`;
    }).join('\n');

    const gapLines = result.gaps.length
      ? result.gaps.map((g, i) => `${i + 1}. ${g}`).join('\n')
      : 'No major gaps identified—maintain current controls.';

    return `IDENTITY & PASSWORD RISK CHECK RESULTS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Identity Risk: ${result.level} (${result.percent}% risk score)
Approx. users / accounts: ${userCount}

${result.blurb}

ANSWERS:
${answerLines}

TOP GAPS TO CLOSE:
${gapLines}`;
  }, [result, answers, userCount]);

  // Intro
  if (status === 'intro') {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <div className="text-center mb-8">
            <div className="text-5xl mb-4">🔐</div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">How exposed are your credentials?</h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-lg mx-auto">
              Six questions on MFA coverage, password manager use, shared and reused passwords, breach exposure, and offboarding. Get an instant Identity Risk score and your top gaps.
            </p>
          </div>

          <div className="bg-[#2813e8]/5 border border-[#2813e8]/20 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-[#2813e8] mb-2">What you&apos;ll learn:</h3>
            <ul className="text-gray-700 dark:text-gray-300 text-sm space-y-1">
              <li>• Whether your identity risk is High, Medium, or Low</li>
              <li>• The specific gaps leaving you exposed</li>
              <li>• What to fix first to close the biggest holes</li>
            </ul>
          </div>

          <button
            onClick={() => setStatus('assessment')}
            className="w-full bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] hover:opacity-90 text-white font-semibold py-3 px-6 rounded-lg transition-opacity"
          >
            Start the Risk Check
          </button>
        </div>
      </div>
    );
  }

  // Assessment
  if (status === 'assessment') {
    const question = QUESTIONS[currentIndex];
    const progress = ((currentIndex + 1) / QUESTIONS.length) * 100;
    const currentAnswer = answers.find((a) => a.questionId === question.id);

    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
              <span>Identity Risk Check</span>
              <span>Question {currentIndex + 1} of {QUESTIONS.length}</span>
            </div>
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">{question.text}</h2>
            {question.helpText && <p className="text-gray-600 dark:text-gray-400 text-sm">{question.helpText}</p>}
          </div>

          <div className="space-y-3 mb-6">
            {question.options.map((option, idx) => (
              <button
                key={idx}
                onClick={() => handleAnswer(idx)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                  currentAnswer?.optionIndex === idx
                    ? 'border-[#2813e8] bg-[#2813e8]/5'
                    : 'border-gray-200 dark:border-gray-700 hover:border-[#2813e8]/40 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <span className="text-gray-900 dark:text-gray-100">{option.text}</span>
              </button>
            ))}
          </div>

          <div className="flex justify-between">
            <button
              onClick={handleBack}
              disabled={currentIndex === 0}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← Back
            </button>
            <button
              onClick={handleRestart}
              className="px-4 py-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Start Over
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Results
  if (status === 'results' && result) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Score */}
        <div className={`rounded-2xl border-2 p-8 ${result.bg}`}>
          <div className="text-center mb-6">
            <div className="text-5xl mb-4">{result.level === 'High' ? '🚨' : result.level === 'Medium' ? '⚠️' : '✅'}</div>
            <h2 className={`text-3xl font-bold mb-2 ${result.color}`}>Identity Risk: {result.level}</h2>
            <p className="text-gray-700 dark:text-gray-300 max-w-xl mx-auto">{result.blurb}</p>
          </div>
          <div className="bg-white/60 dark:bg-gray-800/60 rounded-lg p-4 text-center">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Risk score</div>
            <div className={`text-3xl font-bold ${result.color}`}>{result.percent}%</div>
          </div>
        </div>

        {/* Gaps */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
            {result.gaps.length ? 'Top Gaps to Close' : 'No Major Gaps Identified'}
          </h3>
          {result.gaps.length ? (
            <ul className="space-y-3">
              {result.gaps.map((gap, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-[#2813e8]/10 text-xs font-bold text-[#2813e8]">{i + 1}</span>
                  <span className="text-gray-700 dark:text-gray-300 leading-relaxed">{gap}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-600 dark:text-gray-400">Your controls look solid. A managed service keeps enforcement, monitoring, and offboarding tight as you scale.</p>
          )}
        </div>

        {/* User count */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            How many users / accounts would this cover? <span className="text-[#2813e8] font-bold">{userCount}</span>
          </label>
          <input
            type="range"
            min="1"
            max="500"
            step="1"
            value={userCount}
            onChange={(e) => setUserCount(Number(e.target.value))}
            aria-label="Number of users"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#2813e8]"
          />
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
            <span>1</span>
            <span>250</span>
            <span>500</span>
          </div>
        </div>

        {/* Offer CTA */}
        <div className="rounded-2xl border-2 border-[#2813e8]/30 bg-[#2813e8]/5 p-6 text-center">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Want these gaps closed for you?</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Inventive HQ deploys a business password manager, enforces MFA, and manages access as a flat per-user service.
          </p>
          <Link
            href="/services/password-mfa-management"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] px-8 py-3 font-semibold text-white shadow-lg transition-all hover:scale-105"
          >
            See the Managed Password &amp; MFA Service
          </Link>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleRestart}
            className="flex-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium py-3 px-6 rounded-lg transition-colors"
          >
            🔄 Retake Check
          </button>
        </div>

        {/* Lead capture */}
        <div className="mt-8">
          <LeadCaptureForm
            source="identity"
            title="Get Your Identity Risk Reviewed by an Expert"
            description="Our security team will review your results and send a prioritized plan to close your password, MFA, and access gaps."
            deliverable="a prioritized identity-security plan with per-user pricing"
            resultsSummary={resultsSummary}
          />
        </div>
      </div>
    );
  }

  return null;
}
